import React from "react";
import { useNavigate } from "react-router-dom";
import { Chapter } from "@shared_types";
import StatusChapterCard, { ChapterCardStatus } from "./StatusChapterCard";

/**
 * Web port of the iOS rich ChapterCard for regular (API) course chapters.
 *
 * Source of truth: client-native/src/features/home/components/ChapterCard.tsx:61-209
 * (thumbnail + per-status overlay gradient, center play/check/lock icon, status
 * eyebrow COMPLETED / IN PROGRESS / UP NEXT, title). The card body is shared with
 * the Flight School path via StatusChapterCard.
 *
 * Omitted (iOS-only, no clean web equivalent): the reanimated completion bounce,
 * the glow-pulse ring, and Haptics — these depend on react-native-reanimated /
 * expo-haptics.
 */

const PathConnector: React.FC = () => (
  <div className="flex flex-col items-center py-0">
    <div className="w-0.5 h-3.5 bg-[var(--color-grey-light)]" />
    <div className="w-2 h-2 my-0.5 rounded-full bg-[var(--color-grey-light)]" />
    <div className="w-0.5 h-3.5 bg-[var(--color-grey-light)]" />
  </div>
);

const ChapterCard: React.FC<{ ch: Chapter; onLockedPress?: () => void }> = ({
  ch,
  onLockedPress,
}) => {
  const navigate = useNavigate();

  const isCompleted = ch.status === "completed";
  const isCurrent = ch.status === "current" || ch.status === "in_progress";
  const isLocked = !isCompleted && !isCurrent;

  const status: ChapterCardStatus = isCompleted
    ? "completed"
    : isCurrent
      ? "current"
      : "locked";

  const handlePress = () => {
    if (isLocked) {
      onLockedPress?.();
      return;
    }
    navigate(`/chapter/${ch.chapterId}`);
  };

  return (
    <div>
      <PathConnector />
      <StatusChapterCard status={status} title={ch.title} onPress={handlePress} />
    </div>
  );
};

export default ChapterCard;
export { PathConnector };
